import { Box, MobileStepper } from "@mui/material";
import { optionalClassName, prefixClassName } from "@smartrr/shared/components/primitives";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { getAllProductAndVariantImages } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { useEffect, useMemo, useState } from "react";
import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";

import { useCustomerPortalVariantToPurchasableMap } from "@customer-app/state/reducers/purchasables";

import { StyledFullWidthProductImage } from "../../../../V2SubscriptionManagement/libs/styles";
import { ImageCarouselWrapper } from "../styles";

const CarouselWrapper = autoPlay(SwipeableViews);

interface ImageCarouselProps {
  product: IPurchasable;
  selectedVariant?: IPurchasableVariant | undefined;
}

export const ProductImageCarousel: React.FC<ImageCarouselProps> = ({ product, selectedVariant }) => {
  const variantToPurchasableMap = useCustomerPortalVariantToPurchasableMap();
  const images = useMemo(() => getAllProductAndVariantImages(product, variantToPurchasableMap), []);

  const [activeStep, setActiveStep] = useState<number>(0);

  useEffect(() => {
    if (selectedVariant?.purchasableVariantImages?.length) {
      const correspondingImgIndex = images.findIndex(img =>
        selectedVariant.purchasableVariantImages?.some(vntImg => vntImg === img)
      );
      setActiveStep(correspondingImgIndex);
    }
  }, [selectedVariant]);

  return (
    <ImageCarouselWrapper className={prefixClassName("image-carousel")} data-testid="image-carousel">
      <StyledFullWidthProductImage
        className={prefixClassName("carousel-background-image")}
        src={images[activeStep]}
        overlay
      >
        <CarouselWrapper
          axis="x"
          index={activeStep}
          onChangeIndex={setActiveStep}
          style={{
            zIndex: 3,
            position: "relative",
            overflow: "hidden",
          }}
          // disabling lazyLoading so that all images are accessible via tests
          disableLazyLoading
          enableMouseEvents
        >
          {images.map((src, index) => {
            const isActive = activeStep === index;
            return (
              <div
                key={src}
                className={optionalClassName({
                  "active-carousel-image": isActive,
                  "carousel-image": !isActive,
                })}
              >
                <Box
                  component="img"
                  sx={{
                    height: "350px",
                    display: "block",
                    margin: "0 auto",
                  }}
                  src={src}
                  alt={`${product.purchasableName} image ${index + 1}`}
                />
              </div>
            );
          })}
        </CarouselWrapper>

        {images.length > 1 ? (
          <MobileStepper
            variant="dots"
            steps={images.length}
            activeStep={activeStep}
            className={prefixClassName("pagination")}
            backButton={<React.Fragment />}
            nextButton={<React.Fragment />}
          />
        ) : null}
      </StyledFullWidthProductImage>
    </ImageCarouselWrapper>
  );
};
